import { FOOD_SOURCE_KEYS } from "../../../../constants/foodSourceConstants";

export const RETAIL_INGESTION_LIMITS = Object.freeze({
  MAX_CATEGORIES: 3,
  MAX_PRODUCTS_PER_CATEGORY: 100,
  DEFAULT_MAX_CATEGORIES: 1,
  DEFAULT_MAX_PRODUCTS_PER_CATEGORY: 50,
});

const RETAIL_SOURCE_KEYS = new Set(Object.values(FOOD_SOURCE_KEYS));

export function filterRetailSupermarketSources(sources = []) {
  return sources.filter(
    (source) =>
      source.sourceType === "RETAIL_SUPERMARKET" || RETAIL_SOURCE_KEYS.has(source.sourceKey)
  );
}

export function formatCopPrice(value) {
  if (value == null || value === "") return "—";
  const amount = Number(value);
  if (Number.isNaN(amount)) return "—";
  return `COP ${Math.round(amount).toLocaleString("es-CO")}`;
}

export function formatDateTime(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

export function formatBool(value) {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return "—";
}

export function isValidHttpUrl(value) {
  if (!value || !String(value).trim()) return true;
  try {
    const url = new URL(String(value).trim());
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

export function validateRunForm({
  maxCategories,
  maxProductsPerCategory,
  categoryUrl = "",
  liveUrlValidation = null,
}) {
  const errors = [];
  const categories = Number(maxCategories);
  const products = Number(maxProductsPerCategory);

  if (!Number.isFinite(categories) || categories < 1) {
    errors.push("Max categories must be at least 1.");
  } else if (categories > RETAIL_INGESTION_LIMITS.MAX_CATEGORIES) {
    errors.push(`Max categories cannot exceed ${RETAIL_INGESTION_LIMITS.MAX_CATEGORIES}.`);
  }

  if (!Number.isFinite(products) || products < 1) {
    errors.push("Max products per category must be at least 1.");
  } else if (products > RETAIL_INGESTION_LIMITS.MAX_PRODUCTS_PER_CATEGORY) {
    errors.push(
      `Max products per category cannot exceed ${RETAIL_INGESTION_LIMITS.MAX_PRODUCTS_PER_CATEGORY}.`
    );
  }

  if (categoryUrl && !isValidHttpUrl(categoryUrl)) {
    errors.push("Category URL must be a valid http(s) URL.");
  }

  if (categoryUrl && liveUrlValidation && liveUrlValidation.valid === false) {
    errors.push(
      liveUrlValidation.message || "Selected live category URL is not approved for manual PoC."
    );
  }

  return errors;
}

export function getReadinessBanner(readiness, sourceKey) {
  const key = sourceKey || readiness?.sourceKey || "Source";
  if (readiness?.isRunnable) {
    return `${key} is ready for manual retail ingestion testing.`;
  }
  return `${key} cannot be run yet because discovery is incomplete or the source is blocked.`;
}

export function getRunDisabledReason({
  sourceKey,
  readiness,
  dryRun,
  realRunConfirmed,
  formErrors = [],
  running = false,
}) {
  if (running) return "An ingestion run is already in progress.";
  if (!sourceKey) return "Select a retail supermarket source.";
  if (formErrors.length) return formErrors[0];
  if (!readiness) return "Loading source readiness…";

  if (dryRun && readiness.dryRunAllowed === false) {
    return readiness.blockingReasons?.[0] || "Dry run is not allowed for this source.";
  }

  if (!dryRun) {
    if (readiness.realRunAllowed === false) {
      return readiness.blockingReasons?.[0] || "Real ingestion is not allowed for this source.";
    }
    if (!realRunConfirmed) {
      return "Confirm that you understand this will insert retail price observations.";
    }
  }

  if (readiness.blockingReasons?.length) {
    return readiness.blockingReasons[0];
  }

  return null;
}

export function canRunIngestion(options) {
  return !getRunDisabledReason(options);
}

export function getRunResultMessage(result) {
  if (!result) return null;

  if (result.status === "DRY_RUN_SUCCESS") {
    return `Dry run completed. ${result.productsPrepared ?? 0} products were parsed. No observations were inserted.`;
  }

  if (result.status === "SUCCESS" || result.status === "PARTIAL_SUCCESS") {
    return `Real run completed. ${result.observationsInserted ?? 0} observations were inserted.`;
  }

  if (result.status === "BLOCKED" || result.status === "NOT_CONFIGURED") {
    return "Run blocked. This source failed readiness or safety checks.";
  }

  if (result.status === "NO_PROVIDER_CONFIGURED") {
    return "Provider not configured for this source.";
  }

  if (result.status === "NO_CATEGORY_URLS_CONFIGURED") {
    return "No category URLs configured for this source.";
  }

  if (result.status === "FETCH_FAILED" || result.status === "PARSE_FAILED" || result.status === "FAILED") {
    return result.errors?.[0]?.message || "Retail ingestion run failed.";
  }

  return `Run finished with status ${result.status}.`;
}

const BLOCKING_REASON_LABELS = Object.freeze({
  LIVE_FETCH_NOT_ALLOWED_FOR_STRATEGY:
    "Live fetch is blocked — this source uses verified fixtures for PoC.",
  SOURCE_IS_FIXTURE_ONLY:
    "This source is fixture-only until a live category URL is reviewed and approved.",
  FIXTURE_REQUIRED_FOR_POC:
    "A verified fixture is required for real runs. Use dry run with fixture mode or expand via the ARA ingestion workflow.",
  ARA_CITY_GATE_UNRESOLVED:
    "ARA city/location gate is unresolved — live Antioquia URLs cannot be fetched without reviewed URLs or fixtures.",
  REVIEWED_URL_REQUIRED:
    "The category URL must be reviewed and approved before live fetch.",
});

export function formatBlockingReason(reason) {
  return BLOCKING_REASON_LABELS[reason] || reason;
}

export function parseRetailIngestionApiError(error) {
  const blockingReasons = error?.body?.blockingReasons || [];
  const message =
    error?.body?.message ||
    error?.message ||
    "Retail ingestion request failed.";

  if (
    (error?.body?.error === "SOURCE_NOT_RUNNABLE" || error?.body?.error === "CANNOT_ENABLE") &&
    blockingReasons.length
  ) {
    const formatted = blockingReasons.map(formatBlockingReason).join(" ");
    return `${message} ${formatted}`;
  }

  return message;
}

export function getEnablementBanner(enablement, sourceKey) {
  const key = sourceKey || enablement?.sourceKey || "Source";
  if (enablement?.enabled) {
    return `${key} is enabled for manual retail collection.`;
  }
  if (enablement?.canEnable) {
    return `${key} can be enabled after confirmation.`;
  }
  return `${key} cannot be enabled yet. Resolve blocking reasons before changing registry status.`;
}

export function getEnableDisabledReason({
  sourceKey,
  enablement,
  enableConfirmed = false,
  enabling = false,
}) {
  if (enabling) return "Enablement is in progress.";
  if (!sourceKey) return "Select a retail supermarket source.";
  if (!enablement) return "Load enablement status first.";
  if (enablement.enabled) return "Source is already enabled.";
  if (!enablement.canEnable) {
    return enablement.blockingReasons?.[0] || "Source does not meet enablement requirements.";
  }
  if (!enableConfirmed) {
    return "Confirm that you understand this changes the source registry status.";
  }
  return null;
}

export function getDisableDisabledReason({
  sourceKey,
  enablement,
  disableConfirmed = false,
  disabling = false,
}) {
  if (disabling) return "Disable is in progress.";
  if (!sourceKey) return "Select a retail supermarket source.";
  if (!enablement) return "Load enablement status first.";
  if (!enablement.enabled) return "Source is not currently enabled.";
  if (!disableConfirmed) {
    return "Confirm that you understand this changes the source registry status.";
  }
  return null;
}

export function canEnableSource(options) {
  return !getEnableDisabledReason(options);
}

export function canDisableSource(options) {
  return !getDisableDisabledReason(options);
}
